import axios, { type AxiosResponse, HttpStatusCode } from "axios";
import { create } from "zustand";
import { BankIdStatus } from "@/components/bank-id/types";
import {
  type BankIdRequestHandler,
  withBankIdRequestHandling,
} from "@/components/bank-id/utilities/request-handler";
import { useTranslations } from "@/hooks/use-translations";
import { home } from "@/routes";
import bankId from "@/routes/bank-id";
import type {
  ActivateAuthenticationOrderResponse,
  ActivateAuthenticationOrderResponseData,
  BankIdState,
  BankIdStore,
  CancelAuthenticationOrderResponse,
  CollectAuthenticationOrderStatusResponse,
  CreateAuthenticationOrderResponse,
  CreateAuthenticationOrderResponseData,
  DeviceDetailsResponseData,
  StatusCollectionResponseData,
  SwitchDeviceAuthenticationOrderResponseData,
  SwitchDeviceResponse,
} from "@/stores/bank-id/bank-id-store.types";
import { AuthProcess } from "@/types";

export const STATUS_POLL_INTERVAL_IN_MS = 900;
export const MAX_POLL_TIME_IN_SECONDS = 180; // 3 minutes

export const HINT_CODE_USER_CANCELLATION = "userCancel";

const DEFAULT_LOCALE = "sv_SE";

const initialBankIdState: BankIdState = {
  sessionCreated: false,
  isLoading: false,
  status: BankIdStatus.Untouched,
  locale: DEFAULT_LOCALE,
  intendedUrl: null,
  sessionId: null,
  deviceId: null,
  orderRef: null,
  autoStartToken: null,
  pollIteration: 0,
  secondsLeft: MAX_POLL_TIME_IN_SECONDS,
  isFetchingQrCode: false,
  qrCode: null,
  openOnThisDevice: false,
  isDesktop: false,
  isIos: false,
  isWindowsPhone: false,
  isChrome: false,
  isFirefox: false,
  hintCode: null,
  errorMessage: null,
  successMessage: null,
  authProcess: null,
  acceptedTerms: false,
  showTermsWarning: false,
};

const formatDeviceDetailValues = (values: DeviceDetailsResponseData) => ({
  openOnThisDevice: values.on_this_device,
  isDesktop: values.is_desktop,
  isIos: values.is_ios,
  isWindowsPhone: values.is_windows_phone,
  isChrome: values.is_chrome,
  isFirefox: values.is_firefox,
});

const formatValuesForCreation = (
  values: CreateAuthenticationOrderResponseData
) => ({
  sessionCreated: true,
  isLoading: false,
  status: values.status,
  pollIteration: values.poll_iteration,
  orderRef: values.order_ref,
  locale: values.locale,
  ...formatDeviceDetailValues(values),
});

const formatValuesForStatusCollection = (
  values: StatusCollectionResponseData
) => ({
  status: values.status,
  pollIteration: values.poll_iteration,
  orderRef: values.order_ref,
  qrCode: values.qr_code,
});

const formatValuesForActivation = (
  values: ActivateAuthenticationOrderResponseData
) => ({
  ...formatValuesForStatusCollection(values),
  autoStartToken: values.autostart_token,
});

const formatValuesForSwitchDevice = (
  values: SwitchDeviceAuthenticationOrderResponseData
) => ({
  status: values.status,
  pollIteration: values.poll_iteration,
  qrCode: values.qr_code,
  orderRef: values.order_ref,
  autoStartToken: values.autostart_token,
  openOnThisDevice: values.on_this_device,
  isDesktop: values.is_desktop,
  isIos: values.is_ios,
  isWindowsPhone: values.is_windows_phone,
  isChrome: values.is_chrome,
  isFirefox: values.is_firefox,
});

export const useBankIdStore = create<BankIdStore>((set, get) => ({
  ...initialBankIdState,
  setQrCode: (qrCode: string | null) => set({ qrCode }),
  decrementSecondsLeft: () => set({ secondsLeft: get().secondsLeft - 1 }),
  setIntendedUrl: (intendedUrl: string) => set({ intendedUrl }),
  setIsLoading: (loading: boolean) => set({ isLoading: loading }),
  setErrorMessage: (errorMessage: string | null) => set({ errorMessage }),
  setSuccessMessage: (successMessage: string | null) => set({ successMessage }),
  setAcceptedTerms: (acceptedTerms: boolean) => set({ acceptedTerms }),
  setShowTermsWarning: (showTermsWarning: boolean) => set({ showTermsWarning }),

  switchDevice: async (
    openOnThisDevice: boolean,
    authProcess: AuthProcess,
    callback: (orderRef: string, autoStartToken: string) => void
  ): Promise<void> => {
    // Remove the order ref
    set({ orderRef: null, isLoading: true });

    // Cancel the current order and create a new order with for this/another device
    const response: AxiosResponse<SwitchDeviceResponse> = await axios.post(
      bankId.authentication.order.switchDevice.url(),
      {
        authentication_process: authProcess,
        on_this_device: openOnThisDevice,
      }
    );

    const values = response.data.values;

    set({
      ...formatValuesForSwitchDevice(values),
    });

    callback(values.order_ref as string, values.autostart_token as string);
  },

  doCancelAuthenticationOrder: async (
    hintCode?: string | undefined
  ): Promise<AxiosResponse<CancelAuthenticationOrderResponse>> => {
    const response: AxiosResponse<CancelAuthenticationOrderResponse> =
      await axios.post(bankId.authentication.order.destroy.url(), {
        hint_code: hintCode,
      });

    set({
      ...initialBankIdState,
      status: BankIdStatus.Failed,
      hintCode,
      sessionCreated: true,
      authProcess: get().authProcess,
    });

    return response;
  },

  createAuthenticationOrder: async (
    authProcess: AuthProcess,
    sessionIdFromCallback: string | null
  ) => {
    const response: AxiosResponse<CreateAuthenticationOrderResponse> =
      await axios.post(bankId.authentication.order.create.url(), {
        authentication_process: authProcess,
        session_id_from_callback: sessionIdFromCallback,
      });

    const values = response.data.values;

    if (values.authentication_complete) {
      set({
        isLoading: false,
        authProcess,
        status: BankIdStatus.Complete,
        sessionCreated: true,
      });

      // Don't redirect for DeleteAccount - let the form handle submission
      if (authProcess !== AuthProcess.DeleteAccount) {
        window.location.href = home().url;
      }

      return response;
    }

    set({ ...formatValuesForCreation(values), authProcess });

    return response;
  },

  activateAuthenticationOrder: async () => {
    set({ isFetchingQrCode: true });

    const response: AxiosResponse<ActivateAuthenticationOrderResponse> =
      await axios.post(bankId.authentication.order.store.url(), {
        authentication_process: get().authProcess,
        ...(get().authProcess === AuthProcess.Register && {
          accepted_terms: get().acceptedTerms,
        }),
      });

    set(formatValuesForActivation(response.data.values));

    return response;
  },

  cancelAuthenticationOrder: async (hintCode?: string | undefined) =>
    await get().doCancelAuthenticationOrder(hintCode),

  collectAuthenticationOrderStatus: async () => {
    const response: AxiosResponse<CollectAuthenticationOrderStatusResponse> =
      await axios.post(
        bankId.authentication.order.show.url({
          query: {
            order_ref: get().orderRef,
            authentication_process: get().authProcess,
          },
        })
      );

    const values = response.data.values;
    const status: BankIdStatus = values.status;

    set({
      ...formatValuesForStatusCollection(values),
      hintCode: response.data.hint_code,
      ...formatDeviceDetailValues(values),
    });

    if (status === BankIdStatus.Complete) {
      // Don't redirect for DeleteAccount - let the form handle submission
      if (get().authProcess !== AuthProcess.DeleteAccount) {
        // User is authenticated; redirect to her intended URL.
        window.location.href = get().intendedUrl || home().url;
      }

      return response;
    }

    if (response.status === HttpStatusCode.Ok) {
      return response;
    }

    set({
      isFetchingQrCode: false,
      pollIteration: 0,
    });

    return await get().cancelAuthenticationOrder();
  },
}));

export const useBankIdActions = () => {
  const { __ } = useTranslations();
  const store: BankIdStore = useBankIdStore();

  const requestHandler: BankIdRequestHandler = {
    setErrorMessage: store.setErrorMessage,
    setSuccessMessage: store.setSuccessMessage,
    setIsLoading: store.setIsLoading,
    __,
  };

  const createBankIdAuthentication = async (
    authProcess: AuthProcess,
    sessionIdFromCallback: string | null
  ) => {
    await withBankIdRequestHandling(
      () => store.createAuthenticationOrder(authProcess, sessionIdFromCallback),
      requestHandler
    );
  };

  const activateAuthenticationOrder = async () => {
    await withBankIdRequestHandling(
      () => store.activateAuthenticationOrder(),
      requestHandler
    );
  };

  const cancelAuthenticationOrder = async () => {
    await withBankIdRequestHandling(
      () => store.cancelAuthenticationOrder(),
      requestHandler
    );
  };

  const cancelAuthenticationOrderManually = async (
    hintCode: string | undefined = HINT_CODE_USER_CANCELLATION
  ) => {
    await withBankIdRequestHandling(
      () => store.cancelAuthenticationOrder(hintCode),
      requestHandler
    );
  };

  const collectAuthenticationOrderStatus = async () => {
    await withBankIdRequestHandling(
      () => store.collectAuthenticationOrderStatus(),
      requestHandler
    );
  };

  return {
    handleCreateAuthenticationOrder: createBankIdAuthentication,
    handleActivateAuthenticationOrder: activateAuthenticationOrder,
    handleCancelAuthenticationOrder: cancelAuthenticationOrder,
    handleCancelAuthenticationOrderManually: cancelAuthenticationOrderManually,
    handleCollectAuthenticationOrderStatus: collectAuthenticationOrderStatus,
  };
};
